import cx from 'clsx';
import React from 'react';

import { useDeviceRatio } from '../../hooks/useDeviceRatio';
import IntersectionObserverWrapper from '../IntersectionObserverWrapper';
import JackpotsContainer from './components/JackpotsContainer/JackpotsContainer';
import { JackpotLogo } from './JackpotLogo';
import styles from './JackpotsWrapper.module.scss';
import {
  JackpotStaticTexts,
  WrapperImages,
  WrapperJackpotTiers,
  WrapperSize,
  WrapperType,
  ModernImage,
  Image,
} from './types';

export interface JackpotsWrapperPropsInterface {
  /**
   * Wrapper Type
   */
  type: WrapperType;
  /**
   * Wrapper Size - scaling of the wrapper
   * @default 'slim'
   */
  size?: WrapperSize;
  /**
   * Currency Code - used for formatting jackpot value and retrieving currency value
   * @default 'GBP'
   */
  currencyCode?: string;
  /**
   * Locale - currently used for formatting jackpot value
   * Check `Number.toLocaleString` for more information
   * @default 'en-GB'
   */
  locale?: string;
  /**
   * Images - must contain logo image and can contain background image
   * Supports retina, but does not have to be provided
   * Values must be presented as URLs to the image!
   */
  images: WrapperImages;
  /**
   * Tiers - list of jackpots that should be presented on the wrapper
   * Must contain 3 elements
   */
  tiers: WrapperJackpotTiers;
  /**
   * Translated static text for jackpot messages
   */
  staticTexts: JackpotStaticTexts;

  timestamp: number;
}

const getBackground = (
  type: WrapperType,
  images: WrapperImages,
  windowRatio: number
) => {
  const getUrl = (source: ModernImage | undefined, type: keyof Image) => {
    if (!source) return;
    if (source.modern) {
      return source.modern[type];
    }
    return source[type];
  };

  const imageSrc = images.background?.source;
  const imageBackground =
    getUrl(imageSrc, windowRatio > 1 ? 'retina' : 'normal') ||
    getUrl(imageSrc, 'normal');
  if (imageBackground) {
    return {
      className: null,
      style: { backgroundImage: `url(${imageBackground})` },
    };
  }

  return {
    className:
      type === 'red-tiger'
        ? styles.redTigerBackground
        : styles.vegasMillionsBackground,
    style: {},
  };
};

const JackpotsWrapper = ({
  type,
  size = 'slim',
  currencyCode = 'GBP',
  locale = 'en-GB',
  images,
  tiers,
  staticTexts,
  timestamp,
}: JackpotsWrapperPropsInterface) => {
  const windowRatio = useDeviceRatio().ratio;

  const {
    className: backgroundClassName,
    style: backgroundStyle,
  } = getBackground(type, images, windowRatio);
  return (
    <IntersectionObserverWrapper
      className={cx(styles.container, backgroundClassName)}
      style={backgroundStyle}
      dataTestId="jackpots-wrapper"
    >
      <JackpotLogo logo={images.logo} />
      <JackpotsContainer
        wrapperSize={size}
        wrapperType={type}
        currencyCode={currencyCode}
        locale={locale}
        tiers={tiers}
        staticTexts={staticTexts}
        timestamp={timestamp}
      />
    </IntersectionObserverWrapper>
  );
};

export default JackpotsWrapper;
